import { useCallback, useEffect, useState } from 'react';
import { Badge, Empty, Field, Grid, Note, Panel, Spinner, Stat, TableWrap } from '@wyocspay/ui';
import { dateTime, money, moneyList } from './format.js';
import { ApiError, api, type FeeBillingStatement, type FeeLine, type MerchantFeeStatement } from './api.js';

/**
 * Какво платформата е спечелила от всеки клиент - основата за фактурата.
 *
 * Тук не се издава фактура и никога няма да се издава. Таксата на платформата се
 * удържа от Stripe при всяко плащане, докато то се урежда, тоест парите вече са
 * пристигнали и няма какво да се фактурира. Фактурите се издават от отделната
 * система WYOCS и там е единствената номерация, която трябва да съществува -
 * втора серия за същия месец е счетоводен проблем, не функция.
 *
 * Затова този екран показва числата, а не документа: по търговец, по период, по
 * валута, плюс собствения приход на платформата в долари.
 *
 * Валутите не се събират. Сумите на търговците стоят във валутата, в която са
 * начислени; в долари се превежда само приходът на платформата, всяка преведена
 * сума носи курса, с който е направена, а валута без курс отпада от общата сума
 * и се изписва поименно.
 */

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

/** `2026-07-29`, в UTC, защото сървърът чете границите на периода в UTC. */
function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function monthStart(offset = 0): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
}

function monthEnd(offset = 0): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0));
}

/** „юли 2026“ от ключ „2026-07“, без да се разчита на часова зона. */
function monthName(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('bg-BG', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  );
}

// ---------------------------------------------------------------------------

export function FeeBillingView({ onOpenMerchant }: { onOpenMerchant?: (id: string) => void }) {
  const [from, setFrom] = useState(isoDay(monthStart(-1)));
  const [to, setTo] = useState(isoDay(monthEnd(-1)));
  const [applied, setApplied] = useState({ from: isoDay(monthStart(-1)), to: isoDay(monthEnd(-1)) });
  const [open, setOpen] = useState<string | null>(null);

  const { data, error, loading } = useLoad(() => api.feeBilling(applied), [applied.from, applied.to]);

  const presets: Array<{ label: string; from: string; to: string }> = [
    { label: 'Миналия месец', from: isoDay(monthStart(-1)), to: isoDay(monthEnd(-1)) },
    { label: 'Този месец', from: isoDay(monthStart(0)), to: isoDay(monthEnd(0)) },
    { label: 'Последните 3 месеца', from: isoDay(monthStart(-2)), to: isoDay(monthEnd(0)) },
    {
      label: 'Тази година',
      from: `${new Date().getUTCFullYear()}-01-01`,
      to: `${new Date().getUTCFullYear()}-12-31`,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Това не е фактура и не издава фактура">
        Таксата на платформата се удържа от Stripe при всяко плащане, така че нищо тук не е дължимо -
        парите вече са получени. Екранът показва числата, върху които WYOCS издава своята фактура.
        Номерацията остава там: втора серия номера за същия период би била счетоводен проблем.
      </Note>

      <Panel title="Период">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {presets.map((p) => {
            const active = applied.from === p.from && applied.to === p.to;
            return (
              <button
                key={p.label}
                type="button"
                className={`wc-btn wc-btn-sm ${active ? '' : 'wc-btn-secondary'}`}
                onClick={() => {
                  setFrom(p.from);
                  setTo(p.to);
                  setApplied({ from: p.from, to: p.to });
                  setOpen(null);
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <Field label="От" hint="Включително, по UTC">
            <input className="wc-input wc-num" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="До" hint="Включително, по UTC">
            <input className="wc-input wc-num" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              className="wc-btn"
              disabled={loading || !from || !to || from > to}
              onClick={() => {
                setApplied({ from, to });
                setOpen(null);
              }}
            >
              Покажи
            </button>
          </div>
        </div>

        {from && to && from > to ? <Note tone="warn">Началото на периода е след края му.</Note> : null}
      </Panel>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      {data ? (
        <>
          <Grid cols={3}>
            <Stat
              label={`Приход на платформата · ${data.reportingCurrency.toUpperCase()}`}
              value={money(data.totalEarned.total, data.totalEarned.currency)}
              hint={
                data.totalEarned.estimated > 0
                  ? `от които ${money(data.totalEarned.estimated, data.totalEarned.currency)} преведени`
                  : 'без превалутиране'
              }
              accent="var(--wc-green)"
            />
            <Stat
              label="Клиенти с оборот"
              value={data.merchants.filter((m) => m.lines.length > 0).length}
              hint={`от ${data.merchants.length} общо`}
            />
            <Stat
              label="Период"
              value={`${data.period.days} дни`}
              hint={`${dateTime(data.period.from)} - ${dateTime(data.period.to)}`}
              accent="var(--wc-grey)"
            />
          </Grid>

          <ConversionPanel statement={data} />

          <Panel title="По клиент" padded={false}>
            {data.merchants.length > 0 ? (
              <TableWrap>
                <table className="wc-table">
                  <thead>
                    <tr>
                      <th>Търговец</th>
                      <th>Правило</th>
                      <th>Модел</th>
                      <th className="wc-right">Оборот</th>
                      <th className="wc-right">Такси</th>
                      <th className="wc-right">Приход в USD</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.merchants.map((m) => (
                      <MerchantRows
                        key={m.merchant.id}
                        statement={m}
                        open={open === m.merchant.id}
                        onToggle={() => setOpen(open === m.merchant.id ? null : m.merchant.id)}
                        onOpenMerchant={onOpenMerchant}
                      />
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            ) : (
              <Empty>Няма търговци.</Empty>
            )}
          </Panel>

          <Panel title="Общо за платформата" padded={false}>
            {data.totalLines.length > 0 ? (
              <TableWrap>
                <table className="wc-table">
                  <thead>
                    <tr>
                      <th>Валута</th>
                      <th className="wc-right">Плащания</th>
                      <th className="wc-right">Оборот</th>
                      <th className="wc-right">Рефундирано</th>
                      <th className="wc-right">Такси на платформата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.totalLines.map((line) => (
                      <LineRow key={line.currency} line={line} />
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            ) : (
              <Empty>Няма уредени плащания за този период.</Empty>
            )}
          </Panel>

          <Panel title="Какво трябва да се знае за тези числа">
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--wc-body)', lineHeight: 1.6 }}>
              {data.caveats.map((line, i) => (
                <li key={i} style={{ marginTop: i === 0 ? 0 : 6 }}>{line}</li>
              ))}
            </ul>
          </Panel>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Курсовете, с които са направени преводите, и валутите, за които няма курс.
 *
 * Показва се дори когато няма какво да се превежда, защото „нищо не е
 * превалутирано“ също е факт, който някой трябва да може да прочете, преди да
 * цитира число оттук.
 */
function ConversionPanel({ statement }: { statement: FeeBillingStatement }) {
  const { totalEarned, excludedCurrencies } = statement;

  return (
    <Panel title="Превалутиране">
      {totalEarned.applied.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--wc-muted)' }}>
          Нищо не е превалутирано - целият приход за периода е в{' '}
          {totalEarned.currency.toUpperCase()}.
        </p>
      ) : (
        <TableWrap>
          <table className="wc-table">
            <thead>
              <tr>
                <th>Валута</th>
                <th className="wc-right">Сума</th>
                <th className="wc-right">Курс</th>
                <th>Източник</th>
                <th className="wc-right">В {totalEarned.currency.toUpperCase()}</th>
              </tr>
            </thead>
            <tbody>
              {totalEarned.applied.map((row) => (
                <tr key={row.currency}>
                  <td className="wc-num">{row.currency.toUpperCase()}</td>
                  <td className="wc-right wc-num">{money(row.amount, row.currency)}</td>
                  <td className="wc-right wc-num">{row.rate.toFixed(4)}</td>
                  <td style={{ fontSize: 12.5 }}>
                    {row.source === 'stripe' ? (
                      <>
                        <Badge tone="ok" icon={false}>STRIPE</Badge>
                        {row.referenceProvider ? (
                          <div style={{ color: 'var(--wc-faint)' }}>
                            спрямо {row.referenceProvider}
                            {row.feeRate !== null ? ` · надценка ${(row.feeRate * 100).toFixed(2)}%` : ''}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <Badge tone="warn" icon={false}>ОТ НАСТРОЙКИТЕ</Badge>
                        <div style={{ color: 'var(--wc-faint)' }}>не е котировка на Stripe</div>
                      </>
                    )}
                  </td>
                  <td className="wc-right wc-num" style={{ fontWeight: 600 }}>
                    {money(row.converted, totalEarned.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
        Котировката е курс към даден момент. Stripe превалутира при изплащане и курсът тогава ще бъде
        различен.
      </p>

      {excludedCurrencies.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Note tone="warn" title="Изключени от сумата в долари">
            За {excludedCurrencies.join(', ').toUpperCase()} няма курс, затова тези суми не влизат в
            общата сума вместо да бъдат броени едно към едно. Виждате ги във валутните редове по-долу.
          </Note>
        </div>
      ) : null}
    </Panel>
  );
}

function LineRow({ line }: { line: FeeLine }) {
  return (
    <tr>
      <td className="wc-num">{line.currency.toUpperCase()}</td>
      <td className="wc-right wc-num">{line.payments}</td>
      <td className="wc-right wc-num">{money(line.volume, line.currency)}</td>
      <td className="wc-right wc-num">{money(line.refunded, line.currency)}</td>
      <td className="wc-right wc-num" style={{ fontWeight: 600, color: 'var(--wc-green)' }}>
        {money(line.feesEarned, line.currency)}
      </td>
    </tr>
  );
}

/**
 * Един клиент - обобщено, и по месеци при разгъване.
 *
 * Месеците са в отделен ред, защото това е разбивката, по която се издава
 * фактура, а колона в общата таблица не би я побрала честно.
 */
function MerchantRows({
  statement,
  open,
  onToggle,
  onOpenMerchant,
}: {
  statement: MerchantFeeStatement;
  open: boolean;
  onToggle: () => void;
  onOpenMerchant?: (id: string) => void;
}) {
  const { merchant, rule, lines, earned, byMonth, externalKey } = statement;
  const platformPays = statement.feePayer === 'application';

  return (
    <>
      <tr>
        <td>
          <strong>{merchant.name}</strong>
          <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{merchant.email}</div>
        </td>
        <td style={{ fontSize: 12.5 }}>
          <span className="wc-num">
            {(rule.percentBps / 100).toFixed(2)}%
            {rule.fixed > 0 ? ` + ${money(rule.fixed, merchant.currency)}` : ''}
          </span>
          <div>
            {rule.exemptFromPlatformFee ? (
              <Badge tone="info" icon={false}>ОСВОБОДЕН</Badge>
            ) : rule.isPlatformDefault ? (
              <Badge tone="neutral" icon={false}>ПО ПОДРАЗБИРАНЕ</Badge>
            ) : (
              <Badge tone="warn" icon={false}>ИНДИВИДУАЛНА</Badge>
            )}
          </div>
        </td>
        <td style={{ fontSize: 12.5 }}>
          <Badge tone={platformPays ? 'warn' : 'neutral'} icon={false}>
            {platformPays ? 'ПЛАТФОРМАТА ПЛАЩА STRIPE' : 'ТЪРГОВЕЦЪТ ПЛАЩА STRIPE'}
          </Badge>
        </td>
        <td className="wc-right wc-num">
          {lines.length > 0 ? moneyList(lines.map((l) => ({ currency: l.currency, amount: l.volume }))) : '-'}
        </td>
        <td className="wc-right wc-num" style={{ fontWeight: 600, color: 'var(--wc-green)' }}>
          {lines.length > 0
            ? moneyList(lines.map((l) => ({ currency: l.currency, amount: l.feesEarned })))
            : '-'}
        </td>
        <td className="wc-right wc-num" style={{ fontWeight: 600 }}>
          {money(earned.total, earned.currency)}
          {earned.missingRates.length > 0 ? (
            <div style={{ fontSize: 11.5, color: 'var(--wc-orange)' }}>
              без {earned.missingRates.join(', ').toUpperCase()}
            </div>
          ) : null}
        </td>
        <td className="wc-right" style={{ whiteSpace: 'nowrap' }}>
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={onToggle}
            aria-expanded={open}
          >
            {open ? 'Скрий' : 'По месеци'}
          </button>{' '}
          {onOpenMerchant ? (
            <button
              type="button"
              className="wc-btn wc-btn-sm wc-btn-secondary"
              onClick={() => onOpenMerchant(merchant.id)}
            >
              Отвори
            </button>
          ) : null}
        </td>
      </tr>

      {open ? (
        <tr>
          <td colSpan={7} style={{ background: 'var(--wc-surface-alt)' }}>
            {byMonth.length > 0 ? (
              <table className="wc-table" style={{ background: 'transparent' }}>
                <thead>
                  <tr>
                    <th>Месец</th>
                    <th>Валута</th>
                    <th className="wc-right">Плащания</th>
                    <th className="wc-right">Оборот</th>
                    <th className="wc-right">Рефундирано</th>
                    <th className="wc-right">Такси на платформата</th>
                  </tr>
                </thead>
                <tbody>
                  {byMonth.flatMap((month) =>
                    month.lines.map((line, i) => (
                      <tr key={`${month.month}:${line.currency}`}>
                        <td style={{ fontSize: 13 }}>{i === 0 ? monthName(month.month) : ''}</td>
                        <td className="wc-num">{line.currency.toUpperCase()}</td>
                        <td className="wc-right wc-num">{line.payments}</td>
                        <td className="wc-right wc-num">{money(line.volume, line.currency)}</td>
                        <td className="wc-right wc-num">{money(line.refunded, line.currency)}</td>
                        <td className="wc-right wc-num" style={{ fontWeight: 600, color: 'var(--wc-green)' }}>
                          {money(line.feesEarned, line.currency)}
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            ) : (
              <Empty>Няма уредени плащания за този клиент през периода.</Empty>
            )}

            <p style={{ margin: '10px 12px 12px', fontSize: 12, color: 'var(--wc-faint)' }}>
              Ключ за външна система: <span className="wc-num">{externalKey}</span>. Това не е номер на
              фактура - изгражда се от идентификатора на търговеца и периода и е един и същ при всяко
              зареждане.
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}
